/**
 * Ciel et cycle jour/nuit (§7, étape 9).
 *
 * Sans lui, la ville flotte sur un aplat bleu nuit : c'est ce qui fait « prototype »
 * plutôt que « jeu ». Trois couches, dans cet ordre de dessin :
 *
 *   1. un dégradé de ciel qui change avec l'heure du jeu ;
 *   2. un champ d'étoiles qui n'apparaît que la nuit ;
 *   3. une ombre portée sous le plateau, pour qu'il flotte vraiment.
 *
 * La palette et le cycle vivent dans `core/dayCycle.ts` : ce fichier n'est que la
 * couche PixiJS qui les affiche.
 *
 * **Le cycle est calculé à partir de l'horloge, pas des frames.** Comme le tick
 * économique (§6), il repose sur des horodatages : un onglet masqué pendant vingt
 * minutes retrouve le bon moment de la journée en revenant, au lieu de reprendre là où
 * il s'était arrêté.
 *
 * **Le dégradé est une texture d'un pixel de large.** Redessiner un `Graphics` en
 * dégradé à chaque frame coûte une re-triangulation complète ; une texture de 1 × 64
 * étirée à l'écran ne coûte qu'un quad, et l'interpolation du GPU fait le dégradé. On
 * ne la régénère que lorsque la phase du jour a réellement changé.
 */

import { Container, Graphics, Sprite, Texture } from 'pixi.js';

import { eraHueShift, skyAt } from '@/core/dayCycle';
import type { SkyState } from '@/core/dayCycle';

/** Nombre d'étoiles. Assez pour un ciel habité, assez peu pour rester gratuit. */
const STAR_COUNT = 90;

/** Empreinte d'une couleur, pour ne régénérer la texture que si elle a changé. */
function toHex(rgb: readonly [number, number, number]): number {
  return (rgb[0] << 16) | (rgb[1] << 8) | rgb[2];
}

export class Sky {
  /**
   * Décalage de teinte de l'ère courante, en degrés (ROADMAP 3.9).
   *
   * Zéro pour l'ère 0 : la palette de la première partie est celle qui a été composée et
   * mesurée. Les ères suivantes la décalent d'un écart doré — voir `eraHueShift`.
   */
  private hueShift = 0;

  /** Change l'ère affichée. Sans effet si la teinte ne bouge pas. */
  setEra(era: number): void {
    const next = eraHueShift(era);
    if (next === this.hueShift) return;
    this.hueShift = next;
    // Le ciel se repeint au prochain `update` ; forcer ici doublerait le travail.
  }

  readonly container = new Container();

  private readonly gradient = new Sprite(Texture.WHITE);
  private readonly stars = new Container();
  private readonly plateShadow = new Sprite(Sky.makeShadowTexture());

  private gradientTexture: Texture | null = null;
  private lastKey = '';
  private viewport = { width: 1, height: 1 };

  constructor() {
    this.plateShadow.anchor.set(0.5);
    this.container.addChild(this.gradient, this.stars, this.plateShadow);
    // Le ciel ne capte jamais un geste : il est décor, pas interface.
    this.container.eventMode = 'none';
    this.buildStars();
  }

  /**
   * Étoiles réparties une fois pour toutes, en coordonnées normalisées.
   *
   * Les repositionner à chaque redimensionnement ferait « scintiller » tout le ciel
   * quand la barre d'URL mobile apparaît — un mouvement d'ensemble que l'œil lit comme
   * un défaut, alors qu'une étoile fixe passe inaperçue.
   */
  private starPositions: { x: number; y: number; r: number; a: number }[] = [];

  private buildStars(): void {
    this.starPositions = Array.from({ length: STAR_COUNT }, (_, i) => {
      // Suite déterministe plutôt que `Math.random()` : le ciel est identique d'une
      // session à l'autre, donc une capture de comparaison reste comparable.
      const gx = (Math.sin(i * 12.9898) * 43758.5453) % 1;
      const gy = (Math.sin(i * 78.233) * 12345.6789) % 1;
      return {
        x: Math.abs(gx),
        // Concentrées dans le haut du ciel : des étoiles derrière la ville feraient
        // apparaître le plateau comme une découpe posée sur un fond.
        y: Math.abs(gy) * 0.55,
        r: 0.7 + Math.abs(gx) * 1.6,
        a: 0.35 + Math.abs(gy) * 0.65,
      };
    });

    const g = new Graphics();
    for (const star of this.starPositions) {
      g.circle(star.x, star.y, star.r).fill({ color: 0xffffff, alpha: star.a });
    }
    this.stars.addChild(g);
  }

  resize(width: number, height: number): void {
    this.viewport = { width, height };
    this.gradient.width = width;
    this.gradient.height = height;

    const layer = this.stars.children[0];
    if (layer instanceof Graphics) {
      layer.clear();
      for (const star of this.starPositions) {
        layer
          .circle(star.x * width, star.y * height, star.r)
          .fill({ color: 0xffffff, alpha: star.a });
      }
    }
  }

  /**
   * Ombre portée sous le plateau.
   *
   * **Une texture de dégradé radial, pas des ellipses concentriques.** La première
   * version empilait six ellipses de plus en plus larges : à l'écran, on voyait
   * distinctement les six anneaux — un défaut plus visible que l'absence d'ombre.
   * Un dégradé radial dessiné une fois puis étiré donne un dégradé continu, et ne
   * coûte qu'un quad par frame au lieu de six re-triangulations.
   */
  setPlateShadow(centerX: number, centerY: number, radiusX: number, radiusY: number): void {
    this.plateShadow.position.set(centerX, centerY);
    this.plateShadow.width = radiusX * 2.6;
    this.plateShadow.height = radiusY * 2.6;
  }

  private static makeShadowTexture(): Texture {
    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (context === null) return Texture.WHITE;

    const gradient = context.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    // Le cœur reste discret : une ombre trop dense sous un plateau qui flotte le fait
    // paraître posé sur une tache, pas suspendu au-dessus du vide.
    gradient.addColorStop(0, 'rgb(6 14 30 / 62%)');
    gradient.addColorStop(0.45, 'rgb(6 14 30 / 34%)');
    gradient.addColorStop(1, 'rgb(6 14 30 / 0%)');

    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);

    return Texture.from(canvas);
  }

  update(now: number): void {
    const state = skyAt(now, this.hueShift);

    // Une clé grossière : régénérer la texture à chaque frame pour un dégradé qui
    // bouge d'un niveau toutes les quelques secondes serait du gaspillage pur.
    const key = `${String(toHex(state.top))}:${String(toHex(state.bottom))}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.applyGradient(state);
    }

    this.stars.alpha = state.stars;
    this.stars.visible = state.stars > 0.01;
  }

  private applyGradient(state: SkyState): void {
    const height = 64;
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (context === null) return;

    const gradient = context.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, `rgb(${state.top.join(' ')})`);
    gradient.addColorStop(1, `rgb(${state.bottom.join(' ')})`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 1, height);

    this.gradientTexture?.destroy(true);
    this.gradientTexture = Texture.from(canvas);
    this.gradient.texture = this.gradientTexture;
    this.gradient.width = this.viewport.width;
    this.gradient.height = this.viewport.height;
  }

  destroy(): void {
    this.gradientTexture?.destroy(true);
    this.container.destroy({ children: true });
  }
}
